import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import { DeleteProject } from '@/connection/projects/delete-project';
import { updateWorkingProjectWithIndexedDb } from '@/store/slices/project';
import { useAppDispatch } from '@/store/hooks';
import Button from '@ui/buttons/button/button';

type DeleteProjectModalProps = {
  projectUuid: string;
  projectName: string;
  showDeleteModal: boolean;
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
};

const DeleteProjectModal = ({
  projectUuid,
  projectName,
  showDeleteModal,
  setShowDeleteModal,
}: DeleteProjectModalProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [deleting, setDeleting] = useState(false);

  const handleClose = () => {
    if (deleting) return null;
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!projectUuid || deleting) return null;
    try {
      setDeleting(true);
      await DeleteProject(projectUuid);
      dispatch(updateWorkingProjectWithIndexedDb(null));
      navigate('/choose', { replace: true });
      setShowDeleteModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  if (!projectUuid) return null;
  return (
    <Modal
      open={showDeleteModal}
      customSize={{
        width: '440px',
        height: 'auto',
        maxHeight: '288px',
        minHeight: '288px',
        maxWidth: 'calc(100vw - 80px)',
      }}
      backgroundColor="var(--gray-900)"
      onRequestClose={handleClose}
      RenderHeaderContent={() => {
        return (
          <Container className="padding-l-40 padding-r-40 padding-t-24" background="none" borderRadius="12px 12px 0 0">
            <Text clamp={2} whiteSpace="wrap" tag="h3" color="var(--gray-100)" className="H3 margin-b-8">
              Warning
            </Text>
          </Container>
        );
      }}
      RenderFooterContent={() => {
        return (
          <Container
            background="var(--gray-900)"
            borderRadius="0 0 12px 12px"
            className="flex fd-row jc-flex-end ai-center padding-16"
          >
            <Button
              design="LocalBttnSec"
              height="48px"
              width="95px"
              className="L1 padding-v-8 padding-h-16 margin-r-8 height-48"
              onClick={handleClose}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              style={{ color: 'var(--gray-1200)' }}
              backgroundColor="var(--semantic-red-400)"
              height="48px"
              width="91px"
              className="L1 padding-v-8 padding-h-16 margin-r-8 height-48"
              onClick={handleDelete}
              disabled={deleting}
            >
              <Text color="var(--gray-1200)" className="L1" textAlign="center">
                Delete
              </Text>
            </Button>
          </Container>
        );
      }}
    >
      <Container background="var(--gray-900)" className="padding-l-40 padding-r-40 margin-b-16">
        <Text tag="p" color="var(--gray-400)" className="P2">
          Are you sure you want to delete your project?
        </Text>
      </Container>
      <Container
        background="var(--gray-800)"
        height="64px"
        className="flex ai-center margin-l-40 margin-r-40 padding-16 margin-b-12"
        borderRadius="12px"
      >
        <Text className="H4" clamp={1}>
          {projectName}
        </Text>
      </Container>

      <Text className="P2 margin-l-40" color="var(--semantic-red-400)">
        This cannot be undone
      </Text>
    </Modal>
  );
};

export default DeleteProjectModal;
